import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useUpdateSolarPlantSiteDetailsMutation } from "../../../../../redux/api/generation/generationAPI";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppInput from "../../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";

interface IProps extends ModelContainerProps {
  show: boolean;
  plantId: string;
  initialSiteName?: string;
  initialSiteAlias?: string;
  initialAddress?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  initialCapacity?: number;
  onSuccess?: () => void;
}

interface FormValues {
  siteName: string;
  siteAlias: string;
  address: string;
  latitude: number | undefined;
  longitude: number | undefined;
  capacity: number | undefined;
}

const defaultFormValues: FormValues = {
  siteName: "",
  siteAlias: "",
  address: "",
  latitude: undefined,
  longitude: undefined,
  capacity: undefined,
};

const sanitizeNumber = (val: unknown): number | undefined => {
  if (
    val === undefined ||
    val === null ||
    val === "" ||
    typeof val === "boolean"
  ) {
    return undefined;
  }
  const num = Number(val);
  return Number.isFinite(num) ? num : undefined;
};

const toNullableNumber = (val: unknown): number | null => {
  const num = sanitizeNumber(val);
  return num !== undefined ? num : null;
};

const EditSiteModal = (props: IProps) => {
  const {
    show,
    plantId,
    initialSiteName = "",
    initialSiteAlias = "",
    initialAddress = "",
    initialLatitude = undefined,
    initialLongitude = undefined,
    initialCapacity = undefined,
    onSuccess,
    onClose,
    onCancel,
    ...rest
  } = props;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { ...defaultFormValues },
    mode: "onChange",
  });

  const [triggerUpdate, { isLoading }] =
    useUpdateSolarPlantSiteDetailsMutation();

  useEffect(() => {
    if (show) {
      setValue("siteName", initialSiteName);
      setValue("siteAlias", initialSiteAlias);
      setValue("address", initialAddress);
      setValue("latitude", sanitizeNumber(initialLatitude));
      setValue("longitude", sanitizeNumber(initialLongitude));
      setValue("capacity", sanitizeNumber(initialCapacity));
    } else {
      reset({ ...defaultFormValues });
    }
  }, [
    show,
    initialSiteName,
    initialSiteAlias,
    initialAddress,
    initialLatitude,
    initialLongitude,
    initialCapacity,
    setValue,
    reset,
  ]);

  const onSubmit = (data: FormValues) => {
    triggerUpdate({
      plantId,
      siteName: data.siteName.trim() === "" ? null : data.siteName.trim(),
      siteAlias: data.siteAlias.trim() === "" ? null : data.siteAlias.trim(),
      address: data.address.trim() === "" ? null : data.address.trim(),
      latitude: toNullableNumber(data.latitude),
      longitude: toNullableNumber(data.longitude),
      capacity: toNullableNumber(data.capacity),
    })
      .unwrap()
      .then(() => {
        showSuccessMessage("Site details updated successfully");
        onSuccess?.();
        onClose?.();
      })
      .catch((error: IHttpError) => {
        showErrorMessage(error.message || "Failed to update site details");
      });
  };

  const handleClose = () => {
    onClose?.();
    reset({ ...defaultFormValues });
  };

  const handleCancel = () => {
    onCancel?.();
    reset({ ...defaultFormValues });
  };

  return (
    <ModalContainer
      {...rest}
      show={show}
      onClose={handleClose}
      onCancel={handleCancel}
      onConfirm={handleSubmit(onSubmit)}
      size="modal-md"
      confirmButtonText="Save"
      cancelButtonText="Cancel"
      confirmButtonVariant="blue"
      title="Edit Site"
      isLoading={isLoading}
      disabled={!isValid}
    >
      <div className="px-2 pb-3">
        <Row className="mb-3">
          <Col>
            <AppInput
              label="Site Name"
              placeholder="Enter site name"
              name="siteName"
              register={register("siteName")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <AppInput
              label="Site Alias"
              placeholder="Enter site alias"
              name="siteAlias"
              register={register("siteAlias")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <AppInput
              label="Address"
              placeholder="Enter address"
              name="address"
              register={register("address")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <AppInput
              label="Latitude"
              placeholder="Enter latitude"
              name="latitude"
              type="number"
              register={register("latitude")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <AppInput
              label="Longitude"
              placeholder="Enter longitude"
              name="longitude"
              type="number"
              register={register("longitude")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <AppInput
              label="Capacity"
              placeholder="Enter capacity"
              name="capacity"
              type="number"
              register={register("capacity")}
              errors={errors}
              isRequired={false}
            />
          </Col>
        </Row>
      </div>
    </ModalContainer>
  );
};

export default EditSiteModal;
